'use client';

import React, { useState } from 'react';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import ROICalculator from './ROICalculator';
import ProcessWorkflow from './ProcessWorkflow';
import CaseStudyShowcase from './CaseStudyShowcase';
import ConsultationWidget from './ConsultationWidget';
import PartnershipBadges from './PartnershipBadges';

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: string[];
  engagement: string;
}

const ServicesInteractive = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const services: Service[] = [
    {
      id: 'aiAgents',
      icon: 'CpuChipIcon',
      title: 'AI Agents Development',
      description:
        'Custom intelligent agents powered by Azure OpenAI and Microsoft Copilot framework. We build conversational AI, automation bots, and intelligent assistants that integrate seamlessly with your existing systems.',
      features: [
        'Azure OpenAI integration with GPT-4 and custom models',
        'Microsoft Copilot Studio development and customization',
        'Natural language processing and understanding',
        'Multi-channel deployment (Teams, web, mobile)',
        'Continuous learning and optimization',
      ],
      engagement: 'Project-based',
    },
    {
      id: 'staffing',
      icon: 'UsersIcon',
      title: 'Contract Staffing',
      description:
        'Pre-vetted technology professionals ready to integrate with your team. From individual contributors to complete project teams, we provide skilled talent with Microsoft and AWS expertise.',
      features: [
        'Senior developers with 5+ years experience',
        'Microsoft Azure and AWS certified professionals',
        'Flexible engagement models (hourly, monthly, project)',
        'Rapid onboarding within 2 weeks',
        'Seamless team integration and collaboration',
      ],
      engagement: 'Time & Materials',
    },
    {
      id: 'dataEngineering',
      icon: 'CircleStackIcon',
      title: 'Data Engineering',
      description:
        'End-to-end data solutions from architecture to implementation. We build scalable data pipelines, warehouses, and analytics platforms on Azure and AWS infrastructure.',
      features: [
        'Azure Synapse and AWS Redshift implementations',
        'Real-time streaming with Event Hubs and Kinesis',
        'ETL/ELT pipeline development and automation',
        'Data lake architecture and governance',
        'Power BI and QuickSight integration',
      ],
      engagement: 'Fixed Price',
    },
    {
      id: 'licensing',
      icon: 'KeyIcon',
      title: 'License Reselling',
      description:
        'Authorized Microsoft and AWS license reseller with competitive pricing and expert guidance. We help you optimize your cloud spending and license management.',
      features: [
        'Microsoft 365 and Azure subscriptions',
        'AWS Reserved Instances and Savings Plans',
        'License optimization and cost analysis',
        'Volume licensing and enterprise agreements',
        'Ongoing license management and support',
      ],
      engagement: 'Subscription',
    },
    {
      id: 'copilot',
      icon: 'AcademicCapIcon',
      title: 'Copilot Workshops',
      description:
        'Comprehensive training programs for Microsoft Copilot adoption. From executive briefings to hands-on developer workshops, we accelerate your AI transformation journey.',
      features: [
        'Executive AI strategy and ROI workshops',
        'Microsoft 365 Copilot adoption training',
        'Developer workshops for Copilot Studio',
        'Custom use case development sessions',
        'Change management and adoption support',
      ],
      engagement: 'Workshop',
    },
  ];

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'development', label: 'Development' },
    { id: 'talent', label: 'Talent' },
    { id: 'infrastructure', label: 'Infrastructure' },
    { id: 'training', label: 'Training' },
  ];

  const categoryMapping: { [key: string]: string[] } = {
    all: ['aiAgents', 'staffing', 'dataEngineering', 'licensing', 'copilot'],
    development: ['aiAgents', 'dataEngineering'],
    talent: ['staffing'],
    infrastructure: ['dataEngineering', 'licensing'],
    training: ['copilot'],
  };

  const filteredServices = services.filter((service) =>
    categoryMapping[selectedCategory].includes(service.id)
  );

  const handleConsultClick = () => {
    const widget = document.querySelector('[aria-label="Open consultation form"]') as HTMLButtonElement;
    if (widget) {
      widget.click();
    }
  };

  return (
    <>
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
              Our Service Portfolio
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Comprehensive technology solutions delivered by execution-focused experts
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2.5 rounded-md font-headline font-semibold text-sm transition-all duration-base ${
                    selectedCategory === category.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-foreground hover:bg-muted border border-border'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
                engagement={service.engagement}
                onConsultClick={handleConsultClick}
              />
            ))}
          </div>
        </div>
      </section>

      <PartnershipBadges />
      <ServiceComparison />
      <ROICalculator />
      <ProcessWorkflow />
      <CaseStudyShowcase />
      <ConsultationWidget />
    </>
  );
};

export default ServicesInteractive;